import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import ConstantList, { ACCEPT_CHANGE_PASSWORD } from "app/appConfig";
import jwtAuthService from "app/services/jwtAuthService";
import localStorageService from "app/services/localStorageService";
import { memo, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import ConfirmPopup from "../Component/Popup/ConfirmPopup";
import IconLock from "../Icon/IconLock";
import IconLogout from "../Icon/IconLogout";
import { EXTERNAL_EMBED_CONTENT, EXTERNAL_LINK } from "../LocalConst";
import { removeLeadingSlash } from "../LocalFunction";
import { LayoutContext } from "./LayoutContainer";
import ChangePasswordPopup from "./Popup/ChangePasswordPopup";
/**
 * [NEW_FIX_L2L3_EMBED] 2026-04-28: Helper build path nhúng iframe đồng bộ với module hiện tại
 * Reason: unify xử lý embed cho menu cấp 1/2/3, tránh miss ở nhánh child/sub
 */
const buildEmbeddedPath = (modulePath, menuName, menuPath) => {
  const encodedPath = encodeURIComponent(removeLeadingSlash(menuPath));
  const encodedTitle = encodeURIComponent(menuName);
  return `${ConstantList.ROOT_PATH}${removeLeadingSlash(modulePath)}/embedded-iframe?embeddedTitle=${encodedTitle}&embeddedSrc=${encodedPath}`;
};

/**
 * [NEW_FIX_L2L3_EMBED] 2026-04-28: Helper phân giải target URL dựa trên MenuType
 * Reason: đảm bảo menu cấp 2/3 cũng đi qua cùng một logic target resolver
 */
const resolveMenuTarget = (node, modulePath) => {
  if (node?.type === EXTERNAL_EMBED_CONTENT) {
    return { kind: 'embed', to: buildEmbeddedPath(modulePath, node.name, node.path) };
  }
  if (node?.type === EXTERNAL_LINK) {
    return { kind: 'external', href: node.path };
  }
  return { kind: 'internal', to: ConstantList.ROOT_PATH + removeLeadingSlash(modulePath) + '/' + removeLeadingSlash(node.path) };
};

const getMenuNestedChildren = (item) => item?.children || item?.navigation || [];

const getMenuKey = (item, fallback) => item?.id ?? item?.path ?? fallback;

const getUserRoleAuthorities = () => {
  const authUser = localStorageService.getLoginUser();
  const roles = authUser?.user?.roles ?? authUser?.roles ?? [];
  return roles.map((role) => role?.authority).filter(Boolean);
};

/** auth rỗng/null → mọi user; có auth → user phải có ít nhất một role khớp */
const hasMenuPermission = (item) => {
  const authList = item?.auth;
  if (!Array.isArray(authList) || authList.length === 0) {
    return true;
  }
  const userAuthorities = getUserRoleAuthorities();
  return authList.some((auth) => userAuthorities.includes(auth));
};

const isMenuDisplayable = (item) => {
  if (!item || item?.isVisible === false) {
    return false;
  }
  return hasMenuPermission(item);
};

/** Menu nhóm: cần quyền bản thân và có ít nhất một con/cháu được phép */
const hasVisibleNestedMenu = (item) => {
  if (!isMenuDisplayable(item)) {
    return false;
  }
  const nested = getMenuNestedChildren(item);
  if (nested.length === 0) {
    return true;
  }
  return nested.some((child) => hasVisibleNestedMenu(child));
};

function LayoutSidebar({ module, user }) {
  const { t } = useTranslation();
  const { openSidebar, setOpenSidebar, isScreenSm } = useContext(LayoutContext);

  const [openConfirmLogout, setOpenConfirmLogout] = useState(false);
  const [openChangePassword, setOpenChangePassword] = useState(false);

  const handleRegisterApp = async () => {
    try {
      // Xóa Cache Storage (nếu có)
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map((name) => caches.delete(name)));
      }

      // Xóa localStorage và sessionStorage
      localStorage.clear();

      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.unregister();
        }
      }

      window.location.reload(true);
    } catch (error) {
      console.error("Lỗi khi xóa cache:", error);
    }
  };

  // Kiểm tra menu con có active không
  const checkIsContainsActiveChild = (item) => {
    if (!item) return false;
    if (String(window.location.pathname) === item?.path) {
      return true;
    }
    if (item?.children && item?.children?.length > 0) {
      return item?.children?.some((child) => checkIsContainsActiveChild(child));
    }
    return false;
  };
  return (
    <nav
      id="sidebar"
      className={`sidebar ${openSidebar ? "open-sidebar" : ""}`}
    // onMouseEnter={() => setOpenTemp(true)}
    // onMouseLeave={() => setOpenTemp(false)}
    >
      <div className="user-container">
        <p className="user-img">
          <svg width="143" height="143" viewBox="0 0 143 143" fill="none">
            <path
              d="M126.253 116.987C133.568 108.185 138.656 97.7514 141.086 86.5678C143.516 75.3841 143.216 63.7798 140.213 52.7363C137.21 41.6928 131.591 31.5351 123.831 23.1225C116.072 14.71 106.401 8.28994 95.6356 4.4056C84.8703 0.521257 73.3279 -0.713138 61.9847 0.806837C50.6415 2.32681 39.8312 6.55644 30.4684 13.1379C21.1056 19.7194 13.4656 28.4591 8.19484 38.6177C2.92405 48.7763 0.177503 60.055 0.187527 71.4995C0.191374 88.1367 6.05423 104.241 16.7473 116.987L16.6454 117.073C17.002 117.501 17.4095 117.868 17.7762 118.291C18.2347 118.815 18.7288 119.309 19.2025 119.819C20.6287 121.367 22.0957 122.855 23.6341 124.25C24.1027 124.678 24.5866 125.076 25.0603 125.483C26.6903 126.889 28.3662 128.224 30.1031 129.466C30.3272 129.619 30.531 129.818 30.7551 129.976V129.915C42.6852 138.31 56.9171 142.816 71.5051 142.816C86.0931 142.816 100.325 138.31 112.255 129.915V129.976C112.479 129.818 112.678 129.619 112.907 129.466C114.639 128.218 116.32 126.889 117.95 125.483C118.424 125.076 118.908 124.673 119.376 124.25C120.914 122.85 122.381 121.367 123.808 119.819C124.281 119.309 124.77 118.815 125.234 118.291C125.596 117.868 126.008 117.501 126.365 117.068L126.253 116.987ZM71.5 30.7495C76.0335 30.7495 80.4653 32.0939 84.2347 34.6126C88.0042 37.1313 90.9422 40.7112 92.6771 44.8996C94.412 49.088 94.8659 53.6968 93.9815 58.1432C93.097 62.5897 90.9139 66.6739 87.7082 69.8796C84.5026 73.0853 80.4183 75.2684 75.9719 76.1528C71.5255 77.0373 66.9166 76.5834 62.7282 74.8485C58.5398 73.1135 54.9599 70.1756 52.4412 66.4061C49.9225 62.6366 48.5782 58.2049 48.5782 53.6714C48.5782 47.5921 50.9931 41.7619 55.2918 37.4632C59.5905 33.1645 65.4208 30.7495 71.5 30.7495ZM30.7857 116.987C30.874 110.298 33.5921 103.914 38.3517 99.2143C43.1114 94.5147 49.5299 91.878 56.2188 91.8745H86.7813C93.4701 91.878 99.8887 94.5147 104.648 99.2143C109.408 103.914 112.126 110.298 112.214 116.987C101.043 127.054 86.5381 132.625 71.5 132.625C56.462 132.625 41.9571 127.054 30.7857 116.987Z"
              fill="#9A9A9A"
            />
          </svg>
        </p>
        <h4 className="user-name">{user?.displayName}</h4>
        <div className="flex-center gap-2 mt-2 user-list-btn">
          <button
            className="btn-user tooltip"
            tooltip="Làm mới App"
            onClick={handleRegisterApp}
          >
            <SyncAltIcon />
          </button>
          {ACCEPT_CHANGE_PASSWORD && (
            <button
              className="btn-user tooltip"
              tooltip="Đổi mật khẩu"
              onClick={() => {
                setOpenChangePassword(true);
              }}
            >
              <IconLock />
            </button>
          )}

          <button
            className="btn-user tooltip"
            tooltip="Đăng xuất"
            onClick={() => setOpenConfirmLogout(true)}
          >
            <IconLogout />
          </button>
        </div>
      </div>

      <ul className={"nav-list"}>
        <ul className="nav-list">
          {module?.navigation?.map((item, itemIndex) => {
            const nestedChildren = getMenuNestedChildren(item);
            const hasChildren = nestedChildren.length > 0;

            if (hasChildren) {
              if (!hasVisibleNestedMenu(item)) return null;
            } else if (!isMenuDisplayable(item)) {
              return null;
            }

            // ============================
            // 🔥 CASE 1: MENU CHA (KHÔNG NAVIGATE)
            // ============================
            if (hasChildren) {
              return (
                <Accordion
                  key={getMenuKey(item, `nav-${itemIndex}`)}
                  disableGutters
                  elevation={0}
                  square
                  onClick={
                    !openSidebar ? () => setOpenSidebar(true) : undefined
                  }
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    {Boolean(item?.Icon) && <item.Icon color="#fff" />}
                    <h6 className="links_name">{t(item.name)}</h6>
                  </AccordionSummary>

                  <AccordionDetails>
                    <ul className="sub-list">
                      {nestedChildren.map((child, childIndex) => {
                        const nestedSub = getMenuNestedChildren(child);
                        const hasSub = nestedSub.length > 0;

                        if (hasSub) {
                          if (!hasVisibleNestedMenu(child)) return null;
                        } else if (!isMenuDisplayable(child)) {
                          return null;
                        }

                        // ============================
                        // 🔥 CASE 2: MENU CON CÓ SUBMENU
                        // ============================
                        if (hasSub) {

                          return (
                            <Accordion
                              key={getMenuKey(child, `child-${itemIndex}-${childIndex}`)}
                              disableGutters
                              elevation={0}
                              square
                              className="sub-accordion"
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <h6 className="links_name">{t(child.name)}</h6>
                              </AccordionSummary>

                              <AccordionDetails>
                                <ul className="third-list">
                                  {nestedSub.map((sub, subIndex) => {
                                    if (!isMenuDisplayable(sub)) return null;

                                    const target = resolveMenuTarget(sub, module.path);

                                    if (target.kind === 'external') {
                                      return (
                                        <li key={getMenuKey(sub, `sub-ext-${itemIndex}-${childIndex}-${subIndex}`)} className="item-detail-nav">
                                          <a
                                            href={target.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="link_item"
                                          >
                                            <h6 className="links_name">{t(sub.name)}</h6>
                                          </a>
                                        </li>
                                      );
                                    }

                                    return (
                                      <li key={getMenuKey(sub, `sub-${itemIndex}-${childIndex}-${subIndex}`)} className="item-detail-nav">
                                        {/* [OLD_LOGIC_KEPT] 
                                        <NavLink
                                          to={ConstantList.ROOT_PATH + module.path + "/" + sub.path}
                                          className="link_item"
                                          activeClassName="link_item-active"
                                          onClick={isScreenSm ? () => setOpenSidebar(false) : undefined}
                                        >
                                          <h6 className="links_name">{t(sub.name)}</h6>
                                        </NavLink>
                                        Reason: giữ để đối chiếu/rollback nhanh theo yêu cầu không xóa code cũ */}

                                        <NavLink
                                          to={target.to}
                                          className="link_item"
                                          activeClassName="link_item-active"
                                          isActive={() => target.kind === 'embed' ? (window.location.pathname + window.location.search === target.to) : undefined}
                                          onClick={isScreenSm ? () => setOpenSidebar(false) : undefined}
                                        >
                                          <h6 className="links_name">{t(sub.name)}</h6>
                                        </NavLink>
                                      </li>
                                    );
                                  })}
                                </ul>
                              </AccordionDetails>

                            </Accordion>
                          );
                        }

                        // ============================
                        // 🔥 CASE 3: MENU CON BÌNH THƯỜNG (CÓ NAVIGATE)
                        // ============================
                        // [NEW_FIX_L2L3_EMBED] 2026-04-28: Sử dụng resolver cho menu cấp 2
                        const childTarget = resolveMenuTarget(child, module.path);

                        if (childTarget.kind === 'external') {
                          return (
                            <li key={getMenuKey(child, `child-ext-${itemIndex}-${childIndex}`)} className="item-detail-nav">
                              <a
                                href={childTarget.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link_item"
                              >
                                <h6 className="links_name">{t(child.name)}</h6>
                              </a>
                            </li>
                          );
                        }

                        return (
                          <li key={getMenuKey(child, `child-${itemIndex}-${childIndex}`)} className="item-detail-nav">
                            {/* [OLD_LOGIC_KEPT] 
                            <NavLink
                              to={ConstantList.ROOT_PATH + module.path + "/" + child.path}
                              className="link_item"
                              activeClassName="link_item-active"
                              onClick={isScreenSm ? () => setOpenSidebar(false) : undefined}
                            >
                              <h6 className="links_name">{t(child.name)}</h6>
                            </NavLink>
                            Reason: giữ để đối chiếu/rollback nhanh theo yêu cầu không xóa code cũ */}

                            <NavLink
                              to={childTarget.to}
                              className="link_item"
                              activeClassName="link_item-active"
                              isActive={() => childTarget.kind === 'embed' ? (window.location.pathname + window.location.search === childTarget.to) : undefined}
                              onClick={isScreenSm ? () => setOpenSidebar(false) : undefined}
                            >
                              <h6 className="links_name">{t(child.name)}</h6>
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>

                  </AccordionDetails>
                </Accordion>
              );
            }

            // ============================
            // 🔥 CASE 4: MENU LEVEL 1 KHÔNG CÓ CHILD (NAVI NGAY)
            // ============================
            // [NEW_FIX_L2L3_EMBED] 2026-04-28: Sử dụng resolver thống nhất cho cấp 1
            const itemTarget = resolveMenuTarget(item, module.path);

            /* [OLD_LOGIC_KEPT] 
            let path = item?.path;
            let isExternalLink = false;

            if (item?.type === EXTERNAL_EMBED_CONTENT) {
              const encodedPath = encodeURIComponent(removeLeadingSlash(item?.path));
              const encodedTitle = encodeURIComponent(item?.name);
              path = `embedded-iframe?embeddedTitle=${encodedTitle}&embeddedSrc=${encodedPath}`;
            } else if (item?.type === EXTERNAL_LINK) {
              isExternalLink = true;
              window.open(item?.path, "_blank");
            } else {
              path = item.path;
            }
            Reason: giữ nguyên để rollback/compare theo yêu cầu không xóa code cũ */

            if (itemTarget.kind === 'external') {
              return (
                <li className="item-nav" key={getMenuKey(item, `nav-ext-${itemIndex}`)} title={t(item.name)}>
                  <a
                    href={itemTarget.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link_item"
                  >
                    {Boolean(item?.Icon) && <item.Icon color="#fff" />}
                    <h6 className="links_name">{t(item.name)}</h6>
                  </a>
                </li>
              );
            }

            return (
              <li className="item-nav" key={getMenuKey(item, `nav-${itemIndex}`)} title={t(item.name)}>
                <NavLink
                  onClick={isScreenSm ? () => setOpenSidebar(false) : undefined}
                  to={itemTarget.to}
                  className="link_item"
                  activeClassName="link_item-active"
                  isActive={() => itemTarget.kind === 'embed' ? (window.location.pathname + window.location.search === itemTarget.to) : undefined}
                >
                  {Boolean(item?.Icon) && <item.Icon color="#fff" />}
                  <h6 className="links_name">{t(item.name)}</h6>
                </NavLink>
              </li>
            );
          })}

        </ul>
      </ul>

      <div className="sidebar__footer">
        <button
          className="logout__btn"
          onClick={() => setOpenConfirmLogout(true)}
        >
          <IconLogout className="btn__icon" />
          <h6 className="logout__content">{t("Đăng xuất")}</h6>
        </button>
      </div>

      {openConfirmLogout && (
        <ConfirmPopup
          isNotAPromise
          open={openConfirmLogout}
          onClose={() => setOpenConfirmLogout(false)}
          text={t("Bạn có chắc chắn muốn đăng xuất khỏi tài khoản này?")}
          onConfirm={() => {
            setOpenConfirmLogout(false);
            jwtAuthService.logout();
          }}
        />
      )}

      {openChangePassword && (
        <ChangePasswordPopup
          open={openChangePassword}
          handleClose={() => {
            setOpenChangePassword(false);
          }}
        />
      )}
    </nav>
  );
}

export default memo(LayoutSidebar);
